import React from "react";
import { FaBookOpen, FaExchangeAlt, FaHashtag, FaCalendarAlt, FaFileInvoiceDollar } from "react-icons/fa";
import "./LedgerReport.css";

/**
 * Formats a number with 2 decimal places and thousands separator.
 */
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Returns a CSS class suffix for a transaction type badge.
 */
const typeClass = (type) => {
  const map = {
    Sale: "Sale",
    Receipt: "Receipt",
    Payment: "Payment",
    Refund: "Refund",
    "Credit Note": "CreditNote",
    "Debit Note": "DebitNote",
  };
  return map[type] || "Sale";
};

const LedgerTable = ({ entries = [], summary = {} }) => {
  if (!entries.length) {
    return (
      <div className="ledger-empty-card">
        <div className="ledger-empty-icon">
          <FaBookOpen />
        </div>
        <h3>No Ledger Entries Found</h3>
        <p>No double-entry financial transactions match your selected date range or filter criteria.</p>
      </div>
    );
  }

  return (
    <div className="ledger-table-card">
      {/* Card header */}
      <div className="table-header">
        <div className="table-header-title">
          <FaFileInvoiceDollar style={{ color: "#0284c7" }} />
          <span>Double-Entry General Ledger View</span>
        </div>
        <span className="table-header-count">
          {summary.total_transactions ?? 0} Transaction{(summary.total_transactions ?? 0) !== 1 ? "s" : ""} &bull; {entries.length} Ledger Line{entries.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="ledger-table-wrapper">
        <table className="ledger-table double-entry">
          <thead>
            <tr>
              <th style={{ width: "55px" }}># Txn</th>
              <th>Date</th>
              <th>Voucher / Ref</th>
              <th>Type</th>
              <th>Account Details</th>
              <th>Party / Description</th>
              <th className="num">Debit (Dr)</th>
              <th className="num">Credit (Cr)</th>
              <th className="num">Running Balance</th>
            </tr>
          </thead>

          <tbody>
            {entries.map((entry, idx) => {
              const isDr = entry.entry_type === "Dr";
              const hasDebit = entry.debit > 0;
              const hasCredit = entry.credit > 0;
              const balancePos = entry.running_balance >= 0;

              // Alternating transaction block background tint
              const txnGroupClass = entry.transaction_id % 2 === 0 ? "txn-even" : "txn-odd";
              
              // Standard credit accounts indented
              const accountStyle = isDr
                ? { fontWeight: 600, color: "#0f172a" }
                : { paddingLeft: "24px", color: "#475569", fontStyle: "italic" };

              return (
                <tr key={idx} className={`${txnGroupClass} entry-row ${isDr ? "row-dr" : "row-cr"}`}>
                  <td className="row-index">
                    {isDr ? `#${entry.transaction_id}` : ""}
                  </td>
                  
                  <td className="col-date">
                    {isDr ? entry.date : <span className="subtle-cell-dup">”</span>}
                  </td>
                  
                  <td className="col-voucher">
                    {isDr ? entry.voucher_no : <span className="subtle-cell-dup">”</span>}
                  </td>
                  
                  <td className="col-type">
                    {isDr ? (
                      <span className={`type-badge type-${typeClass(entry.transaction_type)}`}>
                        {entry.transaction_type}
                      </span>
                    ) : (
                      ""
                    )}
                  </td>

                  {/* Account detail columns with dual effect indicator */}
                  <td className="col-account">
                    <div className="account-flex-cell" style={accountStyle}>
                      <span className={`effect-indicator badge-${isDr ? "dr" : "cr"}`}>
                        {isDr ? "Dr" : "Cr"}
                      </span>
                      <span>{isDr ? "" : "To "}{entry.account}</span>
                    </div>
                  </td>

                  {/* Description / Party details */}
                  <td className="col-description">
                    <span className="desc-text">{entry.description}</span>
                    {entry.party && <span className="party-tag">({entry.party})</span>}
                  </td>

                  {/* Debit amount */}
                  <td className="num col-debit">
                    {hasDebit ? (
                      <span className="amount-debit">${fmt(entry.debit)}</span>
                    ) : (
                      <span className="amount-zero">—</span>
                    )}
                  </td>

                  {/* Credit amount */}
                  <td className="num col-credit">
                    {hasCredit ? (
                      <span className="amount-credit">${fmt(entry.credit)}</span>
                    ) : (
                      <span className="amount-zero">—</span>
                    )}
                  </td>

                  {/* Running balance */}
                  <td className="num col-balance">
                    <span className={balancePos ? "running-positive" : "running-negative"}>
                      ${fmt(Math.abs(entry.running_balance))}
                      <span className="balance-suffix">
                        {balancePos ? "Dr" : "Cr"}
                      </span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="ledger-total-row">
              <td colSpan={6} className="total-label">
                GRAND TOTALS
              </td>
              <td className="num amount-debit total-num">${fmt(summary.total_debit)}</td>
              <td className="num amount-credit total-num">${fmt(summary.total_credit)}</td>
              <td className="num total-num">
                <span className={summary.net_balance >= 0 ? "text-primary" : "text-danger"}>
                  ${fmt(Math.abs(summary.net_balance))}
                  <span className="balance-suffix">
                    {summary.net_balance >= 0 ? "Dr" : "Cr"}
                  </span>
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default LedgerTable;
